import React, { useState } from 'react';
import {
  Bell, Package, Receipt, CheckCircle, AlertCircle, Truck,
  Clock, Filter, MoreVertical, X
} from 'lucide-react';

// Mock notifications
const mockNotifications = [
  {
    id: 1,
    type: 'shipment',
    title: 'Shipment Delivered',
    message: 'Package FS-2025-4520 has been delivered to Sarah Williams in Montego Bay.',
    time: '30 min ago',
    read: false
  },
  {
    id: 2,
    type: 'invoice',
    title: 'Invoice Due Soon',
    message: 'Invoice INV-FS-8476 (JMD 12,680) is overdue. Please process payment.',
    time: '2 hours ago',
    read: false
  },
  {
    id: 3,
    type: 'shipment',
    title: 'New Shipment Received',
    message: 'Package FS-2025-4521 received at Miami warehouse. Weight: 12.5 lbs.',
    time: '5 hours ago',
    read: true
  },
  {
    id: 4,
    type: 'transit',
    title: 'Shipment In Transit',
    message: '3 packages have departed Miami and are on their way to Jamaica.',
    time: '1 day ago',
    read: true
  },
  {
    id: 5,
    type: 'payment',
    title: 'Payment Received',
    message: 'Payment of JMD 34,560 for INV-FS-8454 has been confirmed.',
    time: '2 days ago',
    read: true
  },
  {
    id: 6,
    type: 'customer',
    title: 'New Customer',
    message: 'Michael Chen (MC-4102) has been added to your customer list.',
    time: '3 days ago',
    read: true
  },
];

export default function TenantNotifications() {
  const [filter, setFilter] = useState('all');
  const [notifications, setNotifications] = useState(mockNotifications);

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !n.read;
    return n.type === filter;
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  const markAsRead = (id) => {
    setNotifications(notifications.map(n => 
      n.id === id ? { ...n, read: true } : n
    ));
  };

  const markAllAsRead = () => {
    setNotifications(notifications.map(n => ({ ...n, read: true })));
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'shipment': return <Package size={18} />;
      case 'invoice': return <Receipt size={18} />;
      case 'transit': return <Truck size={18} />;
      case 'payment': return <CheckCircle size={18} />;
      case 'customer': return <AlertCircle size={18} />;
      default: return <Bell size={18} />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'shipment': return 'cyan';
      case 'invoice': return 'rose';
      case 'transit': return 'blue';
      case 'payment': return 'lime';
      case 'customer': return 'purple';
      default: return 'gray';
    }
  };

  return (
    <div className="tenant-notifications-page" data-testid="tenant-notifications">
      {/* Header */}
      <div className="notifications-header">
        <div className="unread-count">
          <Bell size={20} />
          <span>{unreadCount} unread notifications</span>
        </div>
        <button 
          className="btn btn-secondary"
          onClick={markAllAsRead}
          disabled={unreadCount === 0}
          data-testid="mark-all-read"
        >
          Mark All as Read
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="notification-filters">
        <button 
          className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All
        </button>
        <button 
          className={`filter-tab ${filter === 'unread' ? 'active' : ''}`}
          onClick={() => setFilter('unread')}
        >
          Unread ({unreadCount})
        </button>
        <button 
          className={`filter-tab ${filter === 'shipment' ? 'active' : ''}`}
          onClick={() => setFilter('shipment')}
        >
          Shipments
        </button>
        <button 
          className={`filter-tab ${filter === 'invoice' ? 'active' : ''}`}
          onClick={() => setFilter('invoice')}
        >
          Invoices
        </button>
        <button 
          className={`filter-tab ${filter === 'payment' ? 'active' : ''}`}
          onClick={() => setFilter('payment')}
        >
          Payments
        </button>
      </div>

      {/* Notifications List */}
      <div className="notifications-list">
        {filteredNotifications.length === 0 ? (
          <div className="empty-state">
            <Bell size={48} />
            <h3>No notifications</h3>
            <p>You're all caught up!</p>
          </div>
        ) : (
          filteredNotifications.map((notification) => (
            <div 
              key={notification.id}
              className={`notification-card ${notification.read ? 'read' : 'unread'}`}
              onClick={() => markAsRead(notification.id)}
              data-testid={`notification-${notification.id}`}
            >
              <div className={`notification-icon ${getTypeColor(notification.type)}`}>
                {getTypeIcon(notification.type)}
              </div>
              
              <div className="notification-content">
                <div className="notification-header">
                  <h4>{notification.title}</h4>
                  {!notification.read && <span className="unread-dot"></span>}
                </div>
                <p className="notification-message">{notification.message}</p>
                <span className="notification-time">
                  <Clock size={12} />
                  {notification.time}
                </span>
              </div>

              <button className="notification-menu" onClick={(e) => e.stopPropagation()}>
                <MoreVertical size={16} />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
